import _ from 'lodash'
import * as XLSX from 'xlsx'
import { useCallback, useMemo } from 'react'
import { ColDef, ColGroupDef } from 'ag-grid-community'
import {
  EntitySearchReferenceEntity,
  shouldHaveEntitySearchReferenceEntity,
  shouldHaveSelectOptions,
  WbsItemAdditionalPropertyEntity,
  WbsItemAdditionalPropertySelectOption,
  WbsItemAdditionalPropertyType,
} from '../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import {
  GroupHeaderLayout,
  PropertyLayout,
  WbsItemAdditionalPropertyLayoutEntity,
} from '../../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { i18nLabelVoService } from '../../../../../domain/value-object/I18nLabelVO'
import {
  ReferenceEntityValueType,
  WbsItemAdditionalPropertyValueObject,
  WbsItemAdditionalPropertyValuesVO,
  wbsItemAdditionalPropertyValuesVoService,
} from '../../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { intl } from '../../../../../i18n'
import { getLabel } from '../../../../../lib/commons/i18nLabel'
import { ProjectMemberProps } from '../../../../../lib/functions/projectMember'
import { SprintDetail } from '../../../../../lib/functions/sprint'
import { TAG_DELIMITER, TagForWbsItem } from '../../../../../lib/functions/tag'
import { TeamProps } from '../../../../../lib/functions/team'
import store from '../../../../../store'
import { toInteger, toNumber } from '../../../../../utils/number'
import { dateTimeValueParser, dateValueParser } from '../../../commons/AgGrid'
import { FlagxsColumnDef } from '../../gridOptions/extension'
import { dateVoService } from '../../../../../domain/value-object/DateVO'
import { NewWbsItemRow } from '../../../../pages/ProjectPlanNew/projectPlanNew'
import { WbsItemStatus } from '../../../../../domain/entity/WbsItemEntity'
import {
  EntitySearchReferenceEntityRepository,
  useEntitySearchReferenceEntityRepositories,
} from '../../../../hooks/useWbsItemAdditionalProperties'
import {
  getTicketTypeFromCacheKey,
  isTicketEntityCacheKey,
  toEntitySearchReferenceEntityCacheKey,
} from '../wbsItemAdditionalProperties/columnDef/entitySearch'

const MULTI_PROPERTY_DELIMITER: string = ','

export type ExcelSource = { [key: string]: string } // key: excel data key(group label), value: excel cell value
export type ExcelKeyMap = { [key: string]: string } // key: external id,  value: excel data key
export type WbsItemAdditionalPropertyEntityMap = {
  [wbsItemAdditionalPropertyUuid: string]: WbsItemAdditionalPropertyEntity
}
export type ExcelImportColumnInfo = {
  keyMap: ExcelKeyMap
  labelMap: { [key: string]: string } // key: external id,  value: column label
  requiredColumnKeys: string[]
  additionalPropertyEntityMap: WbsItemAdditionalPropertyEntityMap
  additionalPropertySelectOptionMap: {
    [wbsItemAdditionalPropertyUuid: string]: {
      [selectOptionLabel: string]: WbsItemAdditionalPropertySelectOption
    }
  }
  additionalPropertyEntitySearchReferenceMap: {
    [entitySearchReferenceEntityCacheKey: string]:
      | {
          repository: EntitySearchReferenceEntityRepository | undefined
          searchCacheKey: string | undefined
        }
      | undefined
  }
}
export type MergeCommonWbsItemUpdatedRowColumnExternalIds = {
  code?: string
  displayName?: string
  status?: string
  substatus?: string
  priority?: string
  difficulty?: string
  description?: string
  team?: string
  accountable?: string
  responsible?: string
  assignee?: string
  watchers?: string
  estimatedStorypoint?: string
  scheduledStartDate?: string
  scheduledEndDate?: string
  actualStartDate?: string
  actualEndDate?: string
  tag?: string
  sprint?: string
}

export const useWbsItemExcelParser = (projectUuid: string) => {
  // initialize functions
  const { getEntitySearchReferenceEntityRepository } =
    useEntitySearchReferenceEntityRepositories(projectUuid)

  const readExcelSource = useCallback((raw: Uint8Array): ExcelSource[] => {
    // Convert excel file
    const workbook = XLSX.read(raw, { type: 'array', dense: true })
    const worksheet = workbook.Sheets[workbook.SheetNames[0]]
    return XLSX.utils.sheet_to_json(worksheet)
  }, [])

  const getLeafColumnDefs = useCallback(
    (
      importColumnExternalIds: string[],
      columnDefs: FlagxsColumnDef[],
      parentHeaderName: string = ''
    ): { def: FlagxsColumnDef; parentHeaderName: string }[] => {
      return (
        columnDefs?.flatMap((def: FlagxsColumnDef) => {
          const groupDef: ColGroupDef = def as ColGroupDef
          if (groupDef.children) {
            return getLeafColumnDefs(
              importColumnExternalIds,
              groupDef.children as FlagxsColumnDef[],
              groupDef.headerName ?? ''
            )
          }
          if (!importColumnExternalIds.includes(def.externalId)) {
            return undefined
          }
          return { def, parentHeaderName }
        }) ?? []
      ).filter(v => !!v) as {
        def: FlagxsColumnDef
        parentHeaderName: string
      }[]
    },
    []
  )

  const getExcelImportColumnInfo = useCallback(
    async (
      importColumnExternalIds: string[],
      columnDefs: FlagxsColumnDef[],
      source: ExcelSource[],
      wbsItemAdditionalProperties:
        | WbsItemAdditionalPropertyLayoutEntity
        | undefined
    ): Promise<ExcelImportColumnInfo> => {
      const info: ExcelImportColumnInfo = {
        keyMap: {},
        labelMap: {},
        requiredColumnKeys: [],
        additionalPropertyEntityMap: {},
        additionalPropertySelectOptionMap: {},
        additionalPropertyEntitySearchReferenceMap: {},
      }

      if (_.isEmpty(source)) return info

      // 2段ヘッダーを考慮した excel column key map.
      // source[0] のキー = 1行目（親グループ名 / 連結セルは __EMPTY 系）, 値 = 2行目（下段ラベル）。
      // 下段ラベルが複数の親グループで重複すると後勝ち上書きで別列の Excel キーを引いてしまうため、
      // "親_子" 形式を主キーとして保持する。子のみのキーはフォールバックとして1個目だけ保持する。
      const importHeader: { [key: string]: string } = {}
      let parentHeaderLabel = ''
      for (const [excelKey, cellValue] of Object.entries(source[0])) {
        if (!excelKey.includes('__EMPTY')) {
          parentHeaderLabel = excelKey
        }
        const childLabel = String(cellValue)
        importHeader[`${parentHeaderLabel}_${childLabel}`] = excelKey
        if (!(childLabel in importHeader)) {
          importHeader[childLabel] = excelKey
        }
      }

      // default column
      const colDefs = getLeafColumnDefs(importColumnExternalIds, columnDefs)

      colDefs.forEach(({ def, parentHeaderName }) => {
        if (def.headerName) {
          const compoundKey = `${parentHeaderName}_${def.headerName}`
          info.keyMap[def.externalId] =
            importHeader[compoundKey] ?? importHeader[def.headerName]
          info.labelMap[def.externalId] = def.headerName
        }
        if ((def as ColDef).cellRendererParams?.uiMeta?.requiredIf) {
          info.requiredColumnKeys.push(def.externalId)
        }
      })

      // additional property label
      wbsItemAdditionalProperties?.groupHeaderLayouts?.forEach(
        (headerLayout: GroupHeaderLayout) => {
          const groupHeaderLabel = i18nLabelVoService.getLabel(
            headerLayout.groupHeader.headerNameI18n
          )
          headerLayout.propertyLayouts?.forEach(
            ({ wbsItemAdditionalProperty }: PropertyLayout) => {
              const label = i18nLabelVoService.getLabel(
                wbsItemAdditionalProperty.propertyNameI18n
              )
              const uuid = wbsItemAdditionalProperty.uuid
              const compoundKey = `${groupHeaderLabel}_${label}`
              info.keyMap[uuid] =
                importHeader[compoundKey] ?? importHeader[label]
              info.labelMap[uuid] = label
              if (wbsItemAdditionalProperty.required) {
                info.requiredColumnKeys.push(wbsItemAdditionalProperty.uuid)
              }
              info.additionalPropertyEntityMap[uuid] = wbsItemAdditionalProperty

              if (
                // Select or MultiSelect
                shouldHaveSelectOptions(wbsItemAdditionalProperty.propertyType)
              ) {
                info.additionalPropertySelectOptionMap[
                  wbsItemAdditionalProperty.uuid
                ] = {}
                wbsItemAdditionalProperty.selectOptions?.forEach(
                  (selectOption: WbsItemAdditionalPropertySelectOption) => {
                    info.additionalPropertySelectOptionMap[
                      wbsItemAdditionalProperty.uuid
                    ][selectOption.selectOption] = selectOption
                  }
                )
              } else if (
                // EntitySearch
                shouldHaveEntitySearchReferenceEntity(
                  wbsItemAdditionalProperty.propertyType
                )
              ) {
                const {
                  entitySearchReferenceEntity: referenceEntity,
                  entitySearchReferenceEntityTicketType: referenceTicketType,
                } = wbsItemAdditionalProperty

                if (referenceEntity) {
                  const cacheKey = toEntitySearchReferenceEntityCacheKey(
                    referenceEntity,
                    referenceTicketType
                  )
                  if (cacheKey) {
                    info.additionalPropertyEntitySearchReferenceMap[cacheKey] =
                      undefined
                  }
                }
              }
            }
          )
        }
      )

      for (let key of Object.keys(
        info.additionalPropertyEntitySearchReferenceMap
      )) {
        if (
          !info.additionalPropertyEntitySearchReferenceMap.hasOwnProperty(key)
        ) {
          continue
        }

        const repository = !isTicketEntityCacheKey(key)
          ? getEntitySearchReferenceEntityRepository(
              key as EntitySearchReferenceEntity
            )
          : getEntitySearchReferenceEntityRepository(
              EntitySearchReferenceEntity.TICKET,
              getTicketTypeFromCacheKey(key)
            )
        info.additionalPropertyEntitySearchReferenceMap[key] = {
          repository,
          searchCacheKey: key,
        }
      }
      return info
    },
    [getLeafColumnDefs, getEntitySearchReferenceEntityRepository]
  )

  // parse functions

  const findFromContext = useCallback(
    (gridOptionContext: object, key: string, value: any) => {
      if (!gridOptionContext) return
      return gridOptionContext[key]?.find(v =>
        [
          v.uuid,
          v.value,
          v.name,
          v.displayName,
          v.officialName,
          v.code,
          getLabel(v.nameI18n),
        ]
          .filter(v => !!v)
          .includes(value)
      )
    },
    []
  )

  const parseBoolean = useCallback((value): boolean => {
    return typeof value === 'string'
      ? value === 'true'
      : typeof value === 'boolean'
        ? value
        : false
  }, [])

  const parseDate = useCallback((value?: string): string | undefined => {
    if (!value) return undefined
    const parsedDateStr = dateValueParser(value?.toString())
    return parsedDateStr
      ? dateVoService.format(dateVoService.construct(parsedDateStr))
      : undefined
  }, [])

  const parseDateTime = useCallback((value?: string): string | undefined => {
    if (!value) return undefined
    return dateTimeValueParser(value?.toString())
  }, [])

  const parseInteger = useCallback((value: string) => toInteger(value), [])
  const parseNumber = useCallback((value: string) => toNumber(value), [])

  const parseSprint = useCallback(
    (
      value: string,
      team: TeamProps | undefined,
      gridOptionContext: any,
      sprintContextKey: string = 'sprint'
    ): SprintDetail | undefined => {
      if (!value) {
        return undefined
      }
      return gridOptionContext[sprintContextKey]?.find(
        v => v.teamUuid === team?.uuid && v.name === value.toString()
      )
    },
    []
  )

  const getTagOptions = useCallback(
    (
      projectUuid?: string,
      gridOptionContext?: any,
      tagContextKey: string = 'tag'
    ) => {
      if (
        gridOptionContext &&
        gridOptionContext.hasOwnProperty(tagContextKey)
      ) {
        return gridOptionContext[tagContextKey]
      }
      const targetProjectUuid = projectUuid ?? store.getState().project.selected
      if (!targetProjectUuid) return undefined
      return store.getState().tag[targetProjectUuid]
    },
    []
  )

  const parseTag = useCallback(
    (
      value: string | undefined,
      projectUuid?: string,
      gridOptionContext?: any,
      tagContextKey: string = 'tag'
    ): TagForWbsItem[] | undefined => {
      const tags: TagForWbsItem[] = getTagOptions(
        projectUuid,
        gridOptionContext,
        tagContextKey
      )
      return value
        ?.toString()
        ?.split(TAG_DELIMITER)
        .map(v => tags.find(tag => tag.name === v))
        .filter((v): v is TagForWbsItem => !!v)
    },
    [getTagOptions]
  )

  const parseWatcher = useCallback(
    (
      value: string | undefined,
      gridOptionContext: any,
      memberContextKey: string = 'member'
    ) => {
      return value
        ?.toString()
        ?.split(MULTI_PROPERTY_DELIMITER)
        .map(v => findFromContext(gridOptionContext, memberContextKey, v))
        .filter(v => v)
    },
    [findFromContext]
  )

  const parseWorkLoadHour = useCallback(
    (value?: string, hoursPerSelectedUnit?: number): number | undefined => {
      const num = toNumber(value)
      if (!num) return undefined
      return num * (hoursPerSelectedUnit || 1)
    },
    []
  )

  // merge functions
  const mergeAdditionalProperty = useCallback(
    async (
      type: WbsItemAdditionalPropertyType,
      uuid: string,
      embodied: WbsItemAdditionalPropertyValuesVO | undefined,
      source: ExcelSource,
      excelInfo: ExcelImportColumnInfo,
      context: any
    ) => {
      const excelColKey = excelInfo.keyMap[uuid]
      if (!excelColKey) return embodied?.values[uuid]
      switch (type) {
        case WbsItemAdditionalPropertyType.NUMBER:
          return parseNumber(source[excelColKey])
        case WbsItemAdditionalPropertyType.TEXT:
        case WbsItemAdditionalPropertyType.MULTI_LINE_TEXT:
          return source[excelColKey]
        case WbsItemAdditionalPropertyType.DATE:
          return parseDate(source[excelColKey])
        case WbsItemAdditionalPropertyType.DATE_TIME:
          return parseDateTime(source[excelColKey])
        case WbsItemAdditionalPropertyType.CHECKBOX:
          return parseBoolean(source[excelColKey])
        case WbsItemAdditionalPropertyType.SELECT: {
          const cellValue = source[excelColKey]
          const optionMap = excelInfo.additionalPropertySelectOptionMap[uuid]
          if (!cellValue || !optionMap) return undefined
          return optionMap[cellValue]?.uuid || undefined
        }
        case WbsItemAdditionalPropertyType.MULTI_SELECT: {
          const cellValues = source[excelColKey]?.split(',').filter(v => !!v)
          const optionMap = excelInfo.additionalPropertySelectOptionMap[uuid]
          if (!optionMap || !cellValues || cellValues.length === 0) {
            return undefined
          }
          return cellValues
            .map(v => optionMap[v]?.uuid)
            .filter(v => !!v)
            .join(',')
        }
        case WbsItemAdditionalPropertyType.ENTITY_SEARCH:
          const referenceEntity =
            excelInfo.additionalPropertyEntityMap[uuid]
              .entitySearchReferenceEntity
          if (!referenceEntity) return
          const referenceTicketType =
            excelInfo.additionalPropertyEntityMap[uuid]
              .entitySearchReferenceEntityTicketType
          const cacheKey = toEntitySearchReferenceEntityCacheKey(
            referenceEntity,
            referenceTicketType
          )
          if (!cacheKey) return
          const reference =
            excelInfo.additionalPropertyEntitySearchReferenceMap[cacheKey]
          if (!reference) return

          const value = source[excelColKey]
          if (!value) return

          // First search by context
          const cachedEntities = reference.searchCacheKey
            ? context[reference.searchCacheKey]
            : undefined
          if (cachedEntities && value in cachedEntities) {
            return cachedEntities[value]
          }

          // If not in context, get it from the server
          const response = await reference?.repository?.search(
            source[excelColKey]
          )
          const entity = response ? response[0] : undefined
          if (reference.searchCacheKey) {
            if (!cachedEntities) {
              context[reference.searchCacheKey] = {
                [value]: entity,
              }
            } else {
              cachedEntities[value] = entity
            }
          }
          return entity
            ? ({
                name: entity.name,
                uuid: entity.uuid,
                iconUrl: entity.iconUrl,
              } as ReferenceEntityValueType)
            : undefined
      }
    },
    [parseNumber, parseDate, parseDateTime, parseBoolean]
  )

  const mergeAdditionalPropertyUpdatedRow = useCallback(
    async (
      embodied: WbsItemAdditionalPropertyValuesVO | undefined,
      source: ExcelSource,
      excelColInfo: ExcelImportColumnInfo,
      context: any
    ) => {
      const entities = Object.entries(excelColInfo.additionalPropertyEntityMap)
      const vo: WbsItemAdditionalPropertyValueObject[] = []
      for (const [uuid, entity] of entities) {
        const margedValue = await mergeAdditionalProperty(
          entity.propertyType,
          uuid,
          embodied,
          source,
          excelColInfo,
          context
        )
        vo.push({
          wbsItemAdditionalPropertyUuid: uuid,
          propertyType: entity.propertyType,
          value: margedValue,
        })
      }
      return wbsItemAdditionalPropertyValuesVoService.of(vo)
    },
    [mergeAdditionalProperty]
  )

  const mergeCommonWbsItemUpdatedRow = useCallback(
    async (
      embodied: Partial<NewWbsItemRow>,
      source: ExcelSource,
      externalIds: MergeCommonWbsItemUpdatedRowColumnExternalIds,
      excelColInfo: ExcelImportColumnInfo,
      context: any,
      teamContextKey: string = 'team',
      statusContextKey: string = 'status',
      substatusContextKey: string = 'substatus',
      priorityContextKey: string = 'priority',
      difficultyContextKey: string = 'difficulty',
      memberContextKey: string = 'member'
    ) => {
      const keyDisplayName = excelColInfo.keyMap[externalIds.displayName || '']
      const keyStatus = excelColInfo.keyMap[externalIds.status || '']
      const keySubstatus = excelColInfo.keyMap[externalIds.substatus || '']
      const keyPriority = excelColInfo.keyMap[externalIds.priority || '']
      const keyDifficulty = excelColInfo.keyMap[externalIds.difficulty || '']
      const keyDescription = excelColInfo.keyMap[externalIds.description || '']
      const keyTeam = excelColInfo.keyMap[externalIds.team || '']
      const keyAccountable = excelColInfo.keyMap[externalIds.accountable || '']
      const keyResponsible = excelColInfo.keyMap[externalIds.responsible || '']
      const keyAssignee = excelColInfo.keyMap[externalIds.assignee || '']
      const keyWatchers = excelColInfo.keyMap[externalIds.watchers || '']
      const keyEstimatedStoryPoint =
        excelColInfo.keyMap[externalIds.estimatedStorypoint || '']
      const keyScheduledStartDate =
        excelColInfo.keyMap[externalIds.scheduledStartDate || '']
      const keyScheduledEndDate =
        excelColInfo.keyMap[externalIds.scheduledEndDate || '']
      const keyActualStartDate =
        excelColInfo.keyMap[externalIds.actualStartDate || '']
      const keyActualEndDate =
        excelColInfo.keyMap[externalIds.actualEndDate || '']
      const keyTags = excelColInfo.keyMap[externalIds.tag || '']
      const keySprint = excelColInfo.keyMap[externalIds.sprint || '']

      const team = keyTeam
        ? findFromContext(context, teamContextKey, source[keyTeam])
        : embodied.team

      const margedAdditionalPropertyRow =
        await mergeAdditionalPropertyUpdatedRow(
          embodied.additionalPropertyValues,
          source,
          excelColInfo,
          context
        )

      return {
        ...embodied,
        displayName: keyDisplayName
          ? source[keyDisplayName].toString()
          : embodied.displayName,
        code: source[excelColInfo.keyMap[externalIds.code || '']]?.toString(),
        status: keyStatus
          ? (findFromContext(context, statusContextKey, source[keyStatus])
              ?.value ?? WbsItemStatus.TODO)
          : embodied.status,
        substatus: keySubstatus
          ? findFromContext(context, substatusContextKey, source[keySubstatus])
              ?.value
          : embodied.substatus,
        priority: keyPriority
          ? findFromContext(context, priorityContextKey, source[keyPriority])
              ?.value
          : embodied.priority,
        difficulty: keyDifficulty
          ? findFromContext(
              context,
              difficultyContextKey,
              source[keyDifficulty]
            )?.value
          : embodied.difficulty,
        description: keyDescription
          ? (source[keyDescription] ?? '').toString()
          : embodied.description,
        team,
        accountable: keyAccountable
          ? findFromContext(context, memberContextKey, source[keyAccountable])
          : embodied.accountable,
        responsible: keyResponsible
          ? findFromContext(context, memberContextKey, source[keyResponsible])
          : embodied.responsible,
        assignee: keyAssignee
          ? findFromContext(context, memberContextKey, source[keyAssignee])
          : embodied.assignee,
        watchers: keyWatchers
          ? parseWatcher(source[keyWatchers], context)
          : embodied.watchers,
        estimatedStoryPoint: keyEstimatedStoryPoint
          ? parseInteger(source[keyEstimatedStoryPoint])
          : embodied.estimatedStoryPoint,
        scheduledDate: {
          startDate: keyScheduledStartDate
            ? parseDate(source[keyScheduledStartDate]) || undefined
            : embodied.scheduledDate?.startDate,
          endDate: keyScheduledEndDate
            ? parseDate(source[keyScheduledEndDate]) || undefined
            : embodied.scheduledDate?.endDate,
        },
        actualDate: {
          startDate: keyActualStartDate
            ? parseDate(source[keyActualStartDate]) || undefined
            : embodied.actualDate?.startDate,
          endDate: keyActualEndDate
            ? parseDate(source[keyActualEndDate]) || undefined
            : embodied.actualDate?.endDate,
        },
        tags: keyTags
          ? parseTag(source[keyTags], embodied.projectUuid)
          : embodied.tags,
        sprint: keySprint
          ? parseSprint(source[keySprint], team, context)
          : embodied.sprint,
        additionalPropertyValues: margedAdditionalPropertyRow,
      } as NewWbsItemRow
    },
    [
      findFromContext,
      parseWatcher,
      parseInteger,
      parseDate,
      parseTag,
      parseSprint,
      mergeAdditionalPropertyUpdatedRow,
    ]
  )

  // utility functions

  const createErrorMessage = useCallback((index, messageId, option?) => {
    const message = intl.formatMessage({ id: messageId }, option)
    return intl.formatMessage({ id: 'validator.index' }, { index, message })
  }, [])

  const watcherNameToString = useCallback(
    (watchers?: ProjectMemberProps[]): string => {
      if (!watchers) return ''
      return (
        watchers
          .map(v => v?.name ?? '')
          .sort()
          .join(MULTI_PROPERTY_DELIMITER) ?? ''
      )
    },
    []
  )

  const hasWbsItemRowDiff = useCallback(
    (a: NewWbsItemRow, b: NewWbsItemRow) => {
      return (
        a.status !== b.status ||
        a.substatus !== b.substatus ||
        a.displayName !== b.displayName ||
        a.team?.uuid !== b.team?.uuid ||
        a.sprint?.uuid !== b.sprint?.uuid ||
        a.accountable?.uuid !== b.accountable?.uuid ||
        a.responsible?.uuid !== b.responsible?.uuid ||
        a.assignee?.uuid !== b.assignee?.uuid ||
        watcherNameToString(a.watchers) !== watcherNameToString(b.watchers) ||
        (a.estimatedStoryPoint ?? 0) !== (b.estimatedStoryPoint ?? 0) ||
        (a.estimatedHour ?? 0) !== (b.estimatedHour ?? 0) ||
        a.scheduledDate?.startDate !== b.scheduledDate?.startDate ||
        a.scheduledDate?.endDate !== b.scheduledDate?.endDate ||
        a.actualDate?.startDate !== b.actualDate?.startDate ||
        a.actualDate?.endDate !== b.actualDate?.endDate ||
        a.priority !== b.priority ||
        a.difficulty !== b.difficulty ||
        a.description !== b.description ||
        (a.estimatedAmount ?? 0) !== (b.estimatedAmount ?? 0) ||
        (a.actualAmount ?? 0) !== (b.actualAmount ?? 0) ||
        a.tags !== b.tags ||
        !_.isEqual(a.additionalPropertyValues, b.additionalPropertyValues)
      )
    },
    [watcherNameToString]
  )

  // result

  const result = useMemo(
    () => ({
      readExcelSource,
      getExcelImportColumnInfo,
      parseBoolean,
      parseDate,
      parseDateTime,
      parseInteger,
      parseNumber,
      parseSprint,
      parseTag,
      parseWatcher,
      parseWorkLoadHour,
      findFromContext,
      mergeCommonWbsItemUpdatedRow,
      mergeAdditionalPropertyUpdatedRow,
      createErrorMessage,
      hasWbsItemRowDiff,
    }),
    [
      readExcelSource,
      getExcelImportColumnInfo,
      parseBoolean,
      parseDate,
      parseDateTime,
      parseInteger,
      parseNumber,
      parseSprint,
      parseTag,
      parseWatcher,
      parseWorkLoadHour,
      findFromContext,
      mergeCommonWbsItemUpdatedRow,
      mergeAdditionalPropertyUpdatedRow,
      createErrorMessage,
      hasWbsItemRowDiff,
    ]
  )

  return result
}
